import React, { useState, useEffect } from 'react';
import { Amplify } from 'aws-amplify';
import { getCurrentUser, signOut as amplifySignOut, fetchUserAttributes } from 'aws-amplify/auth';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import ComicsList from './components/ComicsList';
import AvailsList from './components/AvailsList';
import AvailsManagement from './components/AvailsManagement';
import BookingsList from './components/BookingsList';
import StaffList from './components/StaffList';
import StaffAvailsList from './components/StaffAvailsList';
import Auth from './components/Auth';
import './App.css';

// Configure Amplify with Cognito settings
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: process.env.REACT_APP_USER_POOL_ID || '',
      userPoolClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID || '',
      loginWith: {
        oauth: {
          domain: `${process.env.REACT_APP_COGNITO_DOMAIN}`,
          scopes: ['email', 'openid', 'profile'],
          redirectSignIn: [window.location.origin],
          redirectSignOut: [window.location.origin],
          responseType: 'code',
          providers: ['Google' as any],
        },
      },
    },
  },
});

function NavBar() {
  const location = useLocation();

  return (
    <nav className="nav-bar">
      <Link
        to="/comics"
        className={location.pathname === '/comics' ? 'nav-link active' : 'nav-link'}
      >
        Comics
      </Link>
      <Link
        to="/staff"
        className={location.pathname === '/staff' ? 'nav-link active' : 'nav-link'}
      >
        Staff
      </Link>
      <Link
        to="/avails"
        className={location.pathname === '/avails' ? 'nav-link active' : 'nav-link'}
      >
        Comics Avails
      </Link>
      <Link
        to="/staff-avails"
        className={location.pathname === '/staff-avails' ? 'nav-link active' : 'nav-link'}
      >
        Staff Avails
      </Link>
      <Link
        to="/bookings"
        className={location.pathname === '/bookings' ? 'nav-link active' : 'nav-link'}
      >
        Bookings
      </Link>
      <Link
        to="/avails-management"
        className={location.pathname === '/avails-management' ? 'nav-link active' : 'nav-link'}
      >
        Avails Management
      </Link>
    </nav>
  );
}

function App() {
  const [user, setUser] = useState<any>(null);
  const [userEmail, setUserEmail] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);

      // Fetch user attributes to get email
      try {
        const attributes = await fetchUserAttributes();
        setUserEmail(attributes.email || currentUser.signInDetails?.loginId || currentUser.username);
      } catch (attrErr) {
        console.log('Could not fetch user attributes');
        setUserEmail(currentUser.signInDetails?.loginId || currentUser.username);
      }
    } catch (err) {
      console.log('No user logged in');
      setUser(null);
      setUserEmail('');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await amplifySignOut();
      setUser(null);
      setUserEmail('');
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  if (loading) {
    return (
      <div className="App" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Auth onSuccess={checkUser} />;
  }

  return (
    <Router>
      <div className="App">
        <header className="App-header">
          <div className="header-content">
            <h1>High Line Comedy Club</h1>
            <div className="user-info">
              <span>Welcome, {userEmail || user.username}</span>
              <button onClick={handleSignOut} className="sign-out-button">
                Sign Out
              </button>
            </div>
          </div>
          <NavBar />
        </header>
        <main className="App-main">
          <Routes>
            <Route path="/" element={<Navigate to="/bookings" replace />} />
            <Route path="/comics" element={<ComicsList />} />
            <Route path="/staff" element={<StaffList />} />
            <Route path="/avails" element={<AvailsList />} />
            <Route path="/staff-avails" element={<StaffAvailsList />} />
            <Route path="/bookings" element={<BookingsList />} />
            <Route path="/avails-management" element={<AvailsManagement />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}

export default App;
